import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const GRADE_STYLES = {
  A: { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50', label: 'Hot Lead' },
  B: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', label: 'Strong Prospect' },
  C: { bg: 'bg-yellow-500', text: 'text-yellow-600', light: 'bg-yellow-50', label: 'Moderate' },
  D: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50', label: 'Low Priority' },
};

export default function LeadScoring() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [scoringLead, setScoringLead] = useState(null);

  const fetchData = async () => {
    try {
      const [a, l] = await Promise.all([
        api.get('/api/scoring/analytics'),
        api.get('/api/leads?limit=50'),
      ]);
      setAnalytics(a.data);
      setLeads(l.data.leads || []);
      setLoading(false);
    } catch { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleBatchScore = async () => {
    setScoring(true);
    try {
      const res = await api.post('/api/leads/score/batch');
      toast.success(`Scored ${res.data.scored} leads using ML model v${res.data.model}`);
      fetchData();
    } catch { toast.error('Scoring failed'); }
    setScoring(false);
  };

  const handleScoreLead = async (leadId) => {
    setScoringLead(leadId);
    try {
      const res = await api.post(`/api/leads/${leadId}/score`);
      setSelectedLead(res.data);
      fetchData();
    } catch { toast.error('Failed to score lead'); }
    setScoringLead(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const dist = analytics?.distribution || {};
  const total = Object.values(dist).reduce((a, d) => a + (d.count || 0), 0) || 1;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-scoring-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight flex items-center gap-3"><i className="fa-solid fa-brain text-primary" />Lead Scoring</h1>
          <p className="text-text-muted text-sm mt-1">ML-powered lead qualification and prioritization</p>
        </div>
        <button onClick={handleBatchScore} disabled={scoring} className="btn-primary text-xs py-2.5" data-testid="batch-score-btn">
          {scoring ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Scoring...</> : <><i className="fa-solid fa-wand-magic-sparkles mr-1.5" />Score All Leads</>}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-interactive p-4">
          <div className="flex items-center gap-2 mb-2"><i className="fa-solid fa-database text-primary text-xs" /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Total Leads</span></div>
          <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{analytics?.total_leads || 0}</p>
        </div>
        {['A', 'B', 'C', 'D'].map(g => {
          const gs = GRADE_STYLES[g];
          const d = dist[g] || { count: 0 };
          return (
            <div key={g} className="card-interactive p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Grade {g}</span>
                <span className={`w-6 h-6 rounded-lg ${gs.bg} text-white text-xs font-bold flex items-center justify-center`}>{g}</span>
              </div>
              <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{d.count || 0}</p>
              <p className="text-[10px] text-text-muted">{gs.label}</p>
            </div>
          );
        })}
      </div>

      {/* Grade Distribution Bar */}
      <div className="card-interactive p-5">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Score Distribution</h3>
        <div className="flex rounded-full overflow-hidden h-6 bg-surface-alt">
          {['A', 'B', 'C', 'D'].map(g => {
            const pct = ((dist[g]?.count || 0) / total) * 100;
            if (pct < 1) return null;
            return <div key={g} className={`${GRADE_STYLES[g].bg} flex items-center justify-center text-white text-[10px] font-bold transition-all`} style={{ width: `${pct}%` }}>{pct >= 8 ? `${g}: ${Math.round(pct)}%` : ''}</div>;
          })}
        </div>
        <div className="flex gap-4 mt-3">
          {['A', 'B', 'C', 'D'].map(g => (
            <div key={g} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${GRADE_STYLES[g].bg}`} /><span className="text-xs text-text-muted">Grade {g} ({dist[g]?.count || 0})</span></div>
          ))}
        </div>
      </div>

      {/* Leads with Scores */}
      <div className="card-interactive overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-surface-alt flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Lead Scores</h3>
          <span className="text-[10px] text-text-muted">Model: v2.0</span>
        </div>
        <table className="w-full" data-testid="scoring-table">
          <thead>
            <tr className="border-b border-border">
              {['Lead', 'Score', 'Grade', 'Source', 'Value', 'Action'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-text-muted bg-surface-alt">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.slice(0, 20).map((lead, i) => {
              const g = lead.ml_scoring?.grade;
              const gs = g ? GRADE_STYLES[g] : null;
              return (
                <tr key={lead.id} className="border-b border-border/50 hover:bg-surface-alt/50 transition-colors" data-testid={`score-row-${lead.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">{lead.name?.charAt(0)}</div>
                      <div><p className="text-sm font-semibold text-text-primary">{lead.name}</p><p className="text-[10px] text-text-muted">{lead.city}, {lead.state}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-surface-alt rounded-full overflow-hidden"><div className={`h-full rounded-full ${gs ? gs.bg : 'bg-primary/50'}`} style={{ width: `${lead.score || 0}%` }} /></div>
                      <span className="text-sm font-bold text-text-primary tabular-nums">{lead.score || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{gs ? <span className={`w-7 h-7 rounded-lg ${gs.bg} text-white text-xs font-bold inline-flex items-center justify-center`}>{g}</span> : <span className="text-text-muted text-xs">--</span>}</td>
                  <td className="px-4 py-3 text-xs text-text-secondary capitalize">{lead.source}</td>
                  <td className="px-4 py-3 text-sm font-mono font-semibold text-text-primary">${(lead.estimated_value || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleScoreLead(lead.id)} disabled={scoringLead === lead.id} className="btn-secondary text-xs py-1 px-2.5" data-testid={`score-lead-${lead.id}`}>
                      {scoringLead === lead.id ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-brain mr-1" />Score</>}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Score Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="score-detail-modal">
            <button onClick={() => setSelectedLead(null)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-4">Score Breakdown</h2>
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl ${GRADE_STYLES[selectedLead.grade]?.bg || 'bg-gray-400'} text-white text-3xl font-bold flex items-center justify-center`}>{selectedLead.grade}</div>
              <div>
                <p className="text-3xl font-heading font-bold text-text-primary">{selectedLead.score}/100</p>
                <p className="text-sm text-text-muted">{selectedLead.recommendation}</p>
              </div>
            </div>
            <div className="space-y-2">
              {(selectedLead.factors || []).map((f, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${f.sentiment === 'positive' ? 'bg-green-50' : f.sentiment === 'negative' ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <i className={`fa-solid ${f.sentiment === 'positive' ? 'fa-arrow-up text-green-500' : f.sentiment === 'negative' ? 'fa-arrow-down text-red-500' : 'fa-minus text-gray-400'} text-xs`} />
                    <div><p className="text-sm font-medium text-text-primary">{f.factor}</p><p className="text-[10px] text-text-muted">{f.value}</p></div>
                  </div>
                  <span className={`text-sm font-bold ${f.sentiment === 'positive' ? 'text-green-600' : f.sentiment === 'negative' ? 'text-red-600' : 'text-gray-500'}`}>{f.impact}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-text-muted text-center mt-4">Model: {selectedLead.model_version} &middot; Scored: {new Date(selectedLead.scored_at).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}
